import React from 'react';
import { G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** SHR = Q_s / Q_t — stacked bar with sensible + latent split + pie. */
export function DiagramSensibleHeatRatio() {
  // Stacked bar
  const bx = 60, by = 50, bw = 70, bh = 150;
  // Example proportions (Q_s = 70%, Q_l = 30%)
  const sFrac = 0.7;
  const sH = bh * sFrac;

  return (
    <DiagramCanvas>
      {/* Total bar outline */}
      <Rect x={bx} y={by} width={bw} height={bh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Sensible (bottom) — orange */}
      <Rect x={bx} y={by + bh - sH} width={bw} height={sH} fill="rgba(255,107,26,0.55)" stroke={DT.varColor} strokeWidth="1" />
      {/* Latent (top) — blue */}
      <Rect x={bx} y={by} width={bw} height={bh - sH} fill="rgba(122,170,187,0.45)" stroke={DT.leader} strokeWidth="1" />

      {/* Tick marks (10% increments) */}
      {Array.from({ length: 11 }).map((_, i) => (
        <Line key={i} x1={bx - 5} y1={by + bh - (i * bh / 10)} x2={bx} y2={by + bh - (i * bh / 10)}
          stroke={DT.dim} strokeWidth="1" />
      ))}
      {[0, 25, 50, 75, 100].map((p, i) => (
        <SvgText key={i} x={bx - 8} y={by + bh - (p / 100 * bh) + 3}
          fill={DT.dim} fontSize="7" fontFamily={DT.mono} textAnchor="end">{p}%</SvgText>
      ))}

      {/* Bracket showing Q_s portion */}
      <Line x1={bx + bw + 6} y1={by + bh - sH} x2={bx + bw + 6} y2={by + bh}
        stroke={DT.varColor} strokeWidth="1.2" />
      <Line x1={bx + bw + 2} y1={by + bh - sH} x2={bx + bw + 10} y2={by + bh - sH}
        stroke={DT.varColor} strokeWidth="1.2" />
      <Line x1={bx + bw + 2} y1={by + bh} x2={bx + bw + 10} y2={by + bh}
        stroke={DT.varColor} strokeWidth="1.2" />
      <SvgText x={bx + bw + 14} y={by + bh - sH / 2 + 3} fill={DT.varColor}
        fontSize="10" fontFamily={DT.mono} fontWeight="700">Q_s</SvgText>

      {/* Bracket Q_l */}
      <Line x1={bx + bw + 6} y1={by} x2={bx + bw + 6} y2={by + bh - sH}
        stroke={DT.leader} strokeWidth="1.2" />
      <Line x1={bx + bw + 2} y1={by} x2={bx + bw + 10} y2={by} stroke={DT.leader} strokeWidth="1.2" />
      <SvgText x={bx + bw + 14} y={by + (bh - sH) / 2 + 3} fill={DT.leader}
        fontSize="10" fontFamily={DT.mono} fontWeight="700">Q_l</SvgText>

      {/* Total brace (left) */}
      <Line x1={bx - 28} y1={by} x2={bx - 28} y2={by + bh} stroke={DT.dim} strokeWidth="1" />
      <Line x1={bx - 32} y1={by} x2={bx - 24} y2={by} stroke={DT.dim} strokeWidth="1" />
      <Line x1={bx - 32} y1={by + bh} x2={bx - 24} y2={by + bh} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={bx - 32} y={by + bh / 2 + 3} fill={DT.dim} fontSize="10"
        fontFamily={DT.mono} textAnchor="end" fontWeight="700">Q_t</SvgText>

      {/* SHR meter (right) — gauge from 0..1 */}
      <Rect x={200} y={70} width={120} height={120} fill={DT.fill} stroke={DT.stroke} strokeWidth="1" rx="6" />
      <SvgText x={260} y={88} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">SHR</SvgText>
      {/* Linear scale */}
      <Line x1={216} y1={150} x2={304} y2={150} stroke={DT.dim} strokeWidth="1.5" />
      {[0, 0.25, 0.5, 0.75, 1].map((v, i) => {
        const tx = 216 + 88 * v;
        return (
          <G key={i}>
            <Line x1={tx} y1={150} x2={tx} y2={156} stroke={DT.dim} strokeWidth="1" />
            <SvgText x={tx} y={168} fill={DT.dim} fontSize="7" fontFamily={DT.mono} textAnchor="middle">{v.toFixed(2)}</SvgText>
          </G>
        );
      })}
      {/* SHR = 0.7 indicator */}
      <Polygon points={`${216 + 88 * 0.7},140 ${216 + 88 * 0.7 - 5},132 ${216 + 88 * 0.7 + 5},132`} fill={DT.varColor} />
      <SvgText x={216 + 88 * 0.7} y={128} fill={DT.varColor} fontSize="11" fontFamily={DT.mono} fontWeight="700" textAnchor="middle">0.70</SvgText>
      <SvgText x={260} y={108} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">typical comfort</SvgText>
      <SvgText x={260} y={118} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">cooling: 0.65–0.85</SvgText>

      {/* Labels */}
      <LeaderLine x1={bx - 40} y1={by + bh / 2} x2={20} y2={210} varName="Q_t" desc="total BTU/h" anchor="start" />
      <LeaderLine x1={bx + bw + 30} y1={by + bh - sH / 2} x2={140} y2={210} varName="Q_s" desc="sensible" anchor="middle" />

      <FormulaTag formula="SHR = Q_s / Q_t   (0 = all latent, 1 = all sensible)" />
    </DiagramCanvas>
  );
}
